import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { captureAttribution } from "@/lib/attribution";
import { CartProvider } from "@/lib/cart";
import { ContentProvider } from "@/lib/content";

import Navbar from "@/components/Navbar";
import PromoBar from "@/components/PromoBar";
import SiteFooter from "@/components/SiteFooter";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Legal from "@/pages/Legal";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Appointments from "@/pages/Appointments";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import OrderSuccess from "@/pages/OrderSuccess";
import OrderDetail from "@/pages/OrderDetail";
import OrderTracking from "@/pages/OrderTracking";
import UploadPrescription from "@/pages/UploadPrescription";
import DoctorPartner from "@/pages/DoctorPartner";
import PartnerLogin from "@/pages/PartnerLogin";
import PartnerDashboard from "@/pages/PartnerDashboard";
import PayLink from "@/pages/PayLink";
import Garbhsanskar from "@/pages/Garbhsanskar";
import Retreats from "@/pages/Retreats";
import RetreatDetail from "@/pages/RetreatDetail";
import YogaLanding from "@/pages/YogaLanding";
import YogaDashboard from "@/pages/YogaDashboard";
import YogaLessonPlayer from "@/pages/YogaLessonPlayer";
import YogaBilling from "@/pages/YogaBilling";
import Dashboard from "@/pages/Dashboard";
import Chat from "@/pages/Chat";
import Admin from "@/pages/Admin";
import AuthCallback from "@/pages/AuthCallback";
import "@/App.css";

function Protected({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-12 text-center font-display text-2xl">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

/**
 * Promo strip and navigation are pinned as one block, so the offer stays on screen
 * while scrolling and the nav closes the gap by itself when no promo is running.
 */
function TopBar() {
  return (
    <div className="sticky top-0 z-50" data-testid="site-topbar">
      <PromoBar />
      <Navbar />
    </div>
  );
}

function Shell() {
  const location = useLocation();
  // Any page can carry ?src= / ?ref= — remember it for 30 days, wherever they landed.
  React.useEffect(() => {
    captureAttribution(location.search);
  }, [location.search]);
  // Process a fresh Google session_id BEFORE any protected route runs.
  if (location.hash?.includes("session_id=")) {
    return (
      <div className="min-h-screen bg-paper">
        <TopBar />
        <AuthCallback />
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-paper">
      <TopBar />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/appointments" element={<Appointments />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/about" element={<About />} />
        <Route path="/legal/:doc" element={<Legal />} />
        {/* Gavya now lives inside the main shop as its own category. */}
        <Route path="/brands/gavya" element={<Navigate to="/shop?category=gir-cow" replace />} />
        <Route path="/shop/:id" element={<ProductDetail />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/order-success" element={<OrderSuccess />} />
        <Route path="/orders/:id" element={<OrderDetail />} />
        <Route path="/track" element={<OrderTracking />} />
        <Route path="/upload-prescription" element={<UploadPrescription />} />
        <Route path="/pay/:token" element={<PayLink />} />
        <Route path="/doctor-partner" element={<DoctorPartner />} />
        <Route path="/partner/login" element={<PartnerLogin />} />
        <Route path="/partner" element={<Protected><PartnerDashboard /></Protected>} />
        {/* Samhita library and Community are unpublished for now — the pages and their
            data are intact and still managed from Admin, only public access is off. */}
        <Route path="/samhita" element={<Navigate to="/" replace />} />
        <Route path="/samhita/:id" element={<Navigate to="/" replace />} />
        <Route path="/community" element={<Navigate to="/" replace />} />
        <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
        <Route path="/chat" element={<Protected><Chat /></Protected>} />
        <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />

        {/* Yoga Membership */}
        <Route path="/garbhsanskar" element={<Garbhsanskar />} />
        <Route path="/retreats" element={<Retreats />} />
        <Route path="/retreats/:id" element={<RetreatDetail />} />
        <Route path="/yoga" element={<YogaLanding />} />
        <Route path="/yoga/dashboard" element={<Protected><YogaDashboard /></Protected>} />
        <Route path="/yoga/day/:day" element={<Protected><YogaLessonPlayer /></Protected>} />
        <Route path="/yoga/billing" element={<Protected><YogaBilling /></Protected>} />
      </Routes>
      <SiteFooter />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ContentProvider>
      <CartProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Shell />
        </BrowserRouter>
      </CartProvider>
      </ContentProvider>
    </AuthProvider>
  );
}
